import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

/* ---------- types ---------- */
interface Session {
  id: string;
  agent: string;
  type: string;
  typeVariant: string;
  status: 'success' | 'error' | 'active';
  summary: string;
  userId: string;
  duration: string;
  extra: string;
  time: string;
  messageCount: number;
}

/* ---------- mock data ---------- */
const SESSIONS: Session[] = [
  {
    id: '#a8f2c1',
    agent: 'Emily',
    type: 'Management',
    typeVariant: 'teal',
    status: 'success',
    summary: 'Guest requested a table reservation for 4 people on Saturday evening. Emily delegated to Emily-Reservations which confirmed availability and completed the booking at 7:30 PM.',
    userId: 'user-3847',
    duration: '2.3s total',
    extra: '3 delegations',
    time: '2 min ago',
    messageCount: 8,
  },
  {
    id: '#b4e1d9',
    agent: 'Emily-Reservations',
    type: 'Task',
    typeVariant: 'orange',
    status: 'error',
    summary: 'Reservation check_availability task timed out after 10 seconds. The external API endpoint was unreachable. Guest was informed of the issue and asked to try again.',
    userId: 'user-2190',
    duration: '12.1s total',
    extra: '1 task failure',
    time: '15 min ago',
    messageCount: 4,
  },
  {
    id: '#c7f3a2',
    agent: 'Emily-Menu',
    type: 'Conversational',
    typeVariant: 'blue',
    status: 'success',
    summary: 'Guest asked about gluten-free options. Agent provided a comprehensive list of 6 menu items with prices and ingredient details. Guest seemed satisfied.',
    userId: 'user-5521',
    duration: '0.9s total',
    extra: 'RAG: 3 chunks',
    time: '42 min ago',
    messageCount: 6,
  },
  {
    id: '#d1a8b5',
    agent: 'Martin',
    type: 'General',
    typeVariant: 'green',
    status: 'success',
    summary: 'User asked about company policies regarding remote work. Martin provided a structured answer with 4 key policy points and offered to elaborate.',
    userId: 'user-8834',
    duration: '1.4s total',
    extra: '',
    time: '1 hour ago',
    messageCount: 4,
  },
  {
    id: '#e5c2f1',
    agent: 'Emily',
    type: 'Management',
    typeVariant: 'teal',
    status: 'success',
    summary: 'Multi-turn conversation. Guest first asked about the wine menu (routed to emily-menu), then requested a reservation for 2 (routed to emily-reservations). Both completed successfully.',
    userId: 'user-1203',
    duration: '3.8s total',
    extra: '5 delegations',
    time: '2 hours ago',
    messageCount: 12,
  },
  {
    id: '#f8d4a7',
    agent: 'Emily',
    type: 'Management',
    typeVariant: 'teal',
    status: 'error',
    summary: "Routing failure. Emily couldn't determine which sub-agent to delegate to. The guest asked about catering services which isn't covered by any current sub-agent.",
    userId: 'user-7762',
    duration: '1.6s total',
    extra: 'Routing miss',
    time: '3 hours ago',
    messageCount: 3,
  },
];

/* ---------- styled components ---------- */
const PageBody = styled.div`
  padding: 2rem 0 4rem;
`;

const Container = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 1.5rem;
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
  gap: 1rem;
`;

const PageHeaderLeft = styled.div`
  h1 {
    font-size: 1.5rem;
    margin-bottom: 0.25rem;
  }
  p {
    font-size: 0.875rem;
    color: var(--text-muted);
  }
`;

const SessionsSummary = styled.div`
  display: flex;
  gap: 1.5rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
`;

const SummaryStat = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8125rem;
  color: var(--text-secondary);
`;

const SummaryDot = styled.div<{ $color: string }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
`;

const SummaryValue = styled.span`
  font-weight: 600;
  color: var(--text-primary);
`;

const FiltersBar = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin-bottom: 1.5rem;
`;

const FilterSelect = styled.select`
  padding: 0.5rem 0.875rem;
  background: var(--bg-surface);
  border: 1px solid var(--border);
  border-radius: 8px;
  font-family: inherit;
  font-size: 0.8125rem;
  color: var(--text-primary);
  cursor: pointer;
  outline: none;
  -webkit-appearance: none;
  appearance: none;
  padding-right: 2rem;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%2394a3b8' d='M6 8L1 3h10z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.75rem center;
`;

const SearchBarWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.875rem;
  background: var(--bg-surface);
  border: 1px solid var(--border);
  border-radius: 8px;
  flex: 1;
  min-width: 200px;
  max-width: 320px;
  transition: border-color 0.2s;

  &:focus-within {
    border-color: var(--accent);
  }

  input {
    border: none;
    background: transparent;
    font-family: inherit;
    font-size: 0.8125rem;
    color: var(--text-primary);
    outline: none;
    flex: 1;
    &::placeholder { color: var(--text-muted); }
  }
`;

const SessionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const SessionItem = styled.div`
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 1rem;
  align-items: center;
  padding: 1rem 1.25rem;
  background: var(--bg-surface);
  border: 1px solid var(--border);
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: var(--accent);
    box-shadow: var(--shadow-md);
    transform: translateX(4px);
  }
`;

const TimelineDot = styled.div<{ $status: string }>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  ${({ $status }) =>
    $status === 'success'
      ? 'border: 2px solid var(--success); background: var(--success);'
      : $status === 'error'
        ? 'border: 2px solid var(--error); background: var(--error);'
        : 'border: 2px solid var(--accent); background: var(--accent);'}
`;

const SessionContent = styled.div`
  min-width: 0;
`;

const SessionTopRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.375rem;
  flex-wrap: wrap;
`;

const SessionAgent = styled.span`
  font-size: 0.9375rem;
  font-weight: 600;
  color: var(--text-primary);
`;

const Badge = styled.span<{ $variant?: string }>`
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.625rem;
  border-radius: 999px;
  font-size: 0.6875rem;
  font-weight: 500;
  background: ${({ $variant }) =>
    $variant === 'teal' ? 'var(--accent-subtle)'
      : $variant === 'orange' ? 'var(--warning-subtle)'
        : $variant === 'blue' ? 'var(--info-subtle)'
          : $variant === 'green' ? 'var(--success-subtle)'
            : $variant === 'red' ? 'var(--error-subtle)'
              : 'var(--bg-elevated)'};
  color: ${({ $variant }) =>
    $variant === 'teal' ? 'var(--accent)'
      : $variant === 'orange' ? 'var(--warning)'
        : $variant === 'blue' ? 'var(--info)'
          : $variant === 'green' ? 'var(--success)'
            : $variant === 'red' ? 'var(--error)'
              : 'var(--text-muted)'};
`;

const SessionId = styled.span`
  font-size: 0.75rem;
  color: var(--text-muted);
  font-family: 'SF Mono', 'Fira Code', monospace;
`;

const SessionSummary = styled.div`
  font-size: 0.8125rem;
  color: var(--text-secondary);
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const AILabel = styled.span`
  color: var(--accent);
  font-weight: 500;
  font-size: 0.6875rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
`;

const SessionMetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-top: 0.5rem;
  flex-wrap: wrap;
`;

const SessionMeta = styled.span`
  font-size: 0.75rem;
  color: var(--text-muted);
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

const SessionRight = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.5rem;
  flex-shrink: 0;
`;

const SessionTime = styled.span`
  font-size: 0.75rem;
  color: var(--text-muted);
  white-space: nowrap;
`;

const MsgCount = styled.span`
  font-size: 0.6875rem;
  font-weight: 500;
  color: var(--text-muted);
  background: var(--bg-elevated);
  padding: 0.25rem 0.5rem;
  border-radius: 999px;
`;

const Pagination = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
  margin-top: 2rem;
`;

const PageBtn = styled.button<{ $active?: boolean }>`
  width: 36px;
  height: 36px;
  border-radius: 8px;
  border: 1px solid ${({ $active }) => ($active ? 'var(--accent)' : 'var(--border)')};
  background: ${({ $active }) => ($active ? 'var(--accent)' : 'var(--bg-surface)')};
  color: ${({ $active }) => ($active ? 'white' : 'var(--text-secondary)')};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
  cursor: pointer;
  font-family: inherit;
  font-weight: 500;
  transition: all 0.2s;

  &:hover {
    border-color: var(--accent);
    color: var(--accent);
  }
`;

/* ---------- component ---------- */
const Sessions: React.FC = () => {
  const navigate = useNavigate();

  return (
    <PageBody className="fade-in">
      <Container>
        <PageHeader>
          <PageHeaderLeft>
            <h1>Sessions</h1>
            <p>Browse and analyze agent conversation sessions</p>
          </PageHeaderLeft>
        </PageHeader>

        <SessionsSummary>
          <SummaryStat>
            <SummaryDot $color="var(--success)" />
            <SummaryValue>234</SummaryValue> successful
          </SummaryStat>
          <SummaryStat>
            <SummaryDot $color="var(--error)" />
            <SummaryValue>13</SummaryValue> failed
          </SummaryStat>
          <SummaryStat>
            <SummaryDot $color="var(--accent)" />
            <SummaryValue>1.2s</SummaryValue> avg response
          </SummaryStat>
          <SummaryStat>
            <SummaryDot $color="var(--warning)" />
            <SummaryValue>247</SummaryValue> total this week
          </SummaryStat>
        </SessionsSummary>

        <FiltersBar>
          <FilterSelect>
            <option>All Agents</option>
            <option>Emily</option>
            <option>Emily-Menu</option>
            <option>Emily-Reservations</option>
            <option>Martin</option>
            <option>Data Extractor</option>
          </FilterSelect>
          <FilterSelect>
            <option>All Status</option>
            <option>Successful</option>
            <option>Failed</option>
            <option>In Progress</option>
          </FilterSelect>
          <FilterSelect>
            <option>Last 7 days</option>
            <option>Last 24 hours</option>
            <option>Last 30 days</option>
            <option>All time</option>
          </FilterSelect>
          <SearchBarWrap>
            <span style={{ color: 'var(--text-muted)' }}>&#x1F50D;</span>
            <input placeholder='Search sessions or ask AI...' />
          </SearchBarWrap>
        </FiltersBar>

        <SessionsList>
          {SESSIONS.map((s) => (
            <SessionItem key={s.id} onClick={() => navigate(`/sessions/${s.id.replace('#', '')}`)}>
              <TimelineDot $status={s.status} />
              <SessionContent>
                <SessionTopRow>
                  <SessionAgent>{s.agent}</SessionAgent>
                  <Badge $variant={s.typeVariant}>{s.type}</Badge>
                  <SessionId>{s.id}</SessionId>
                </SessionTopRow>
                <SessionSummary>
                  <AILabel>AI Summary: </AILabel>
                  {s.summary}
                </SessionSummary>
                <SessionMetaRow>
                  <SessionMeta>&#x1F464; {s.userId}</SessionMeta>
                  <SessionMeta>&#x23F1; {s.duration}</SessionMeta>
                  {s.extra && <SessionMeta>&#x1F504; {s.extra}</SessionMeta>}
                </SessionMetaRow>
              </SessionContent>
              <SessionRight>
                <SessionTime>{s.time}</SessionTime>
                <MsgCount>{s.messageCount} messages</MsgCount>
                <Badge $variant={s.status === 'success' ? 'green' : 'red'}>
                  {s.status === 'success' ? 'Success' : 'Failed'}
                </Badge>
              </SessionRight>
            </SessionItem>
          ))}
        </SessionsList>

        <Pagination>
          <PageBtn>&larr;</PageBtn>
          <PageBtn $active>1</PageBtn>
          <PageBtn>2</PageBtn>
          <PageBtn>3</PageBtn>
          <PageBtn>...</PageBtn>
          <PageBtn>12</PageBtn>
          <PageBtn>&rarr;</PageBtn>
        </Pagination>
      </Container>
    </PageBody>
  );
};

export default Sessions;
